<script>
import bem from '@/utils/bem';
import PencilIcon from '@/components/svg/PencilIcon/PencilIcon.vue';
import EditTitleWithLinesModal from '@/components/pageContent/modules/EditTitleWithLinesModal.vue';
import EditDescriptionModal from '@/components/pageContent/modules/EditDescriptionModal.vue';
import EditButtonModal from '@/components/pageContent/modules/EditButtonModal.vue';
import ImageUploadModal from '@/components/pageContent/modules/ImageUploadModal.vue';
import autographBlack from '@/assets/zachary_signature_black.png';
import autographWhite from '@/assets/zachary_signature_white.png';
import TitleWithLinesBlock from './title-with-lines-block.vue';
import CustomImage from './custom-image.vue';
import styles from './new-banner-with-image-block-style.scss';

const b = bem('new-banner-with-image-block', styles);

export default {
  name: 'NewBannerWithImageBlock',
  components: {
    PencilIcon,
    EditTitleWithLinesModal,
    EditDescriptionModal,
    EditButtonModal,
    ImageUploadModal,
    TitleWithLinesBlock,
    CustomImage,
  },
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
    isMobileView: {
      type: Boolean,
      default: false,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
    pageKey: {
      type: String,
      default: '',
    },
  },
  emits: ['update'],
  data() {
    return {
      firstTitleModalVisible: false,
      secondTitleModalVisible: false,
      descriptionModalVisible: false,
      buttonModalVisible: false,
      imageModalVisible: false,
      autographBlack,
      autographWhite,
    };
  },
  computed: {
    isMobile() {
      return this.isMobileView;
    },
    withGrayBackground() {
      return this.data.withGrayBackground || false;
    },
    firstTitle() {
      return this.data.firstTitle || [];
    },
    secondTitle() {
      return this.data.secondTitle || [];
    },
    description() {
      return this.data.description || '';
    },
    descriptionMobile() {
      return this.data.descriptionMobile || this.description;
    },
    displayDescription() {
      return this.isMobile ? this.descriptionMobile : this.description;
    },
    buttonText() {
      return this.data.buttonText || '';
    },
    buttonTextMobile() {
      return this.data.buttonTextMobile || this.buttonText;
    },
    displayButtonText() {
      return this.isMobile ? this.buttonTextMobile : this.buttonText;
    },
    buttonLink() {
      return this.data.buttonLink || '';
    },
    isDarkButton() {
      return this.data.isDarkButton || false;
    },
    isRight() {
      return this.data.isRight || false;
    },
    mediaData() {
      return this.data.mediaData || {};
    },
    displayImage() {
      if (this.isMobile && this.mediaData.srcMobile) {
        return this.mediaData.srcMobile;
      }
      return this.mediaData.src || '';
    },
    hasDescription() {
      return !!this.description;
    },
    hasButton() {
      return !!this.buttonText;
    },
    withAutograph() {
      return this.data.withAutograph || false;
    },
    isWhiteSignature() {
      return this.data.isWhiteSignature || false;
    },
    tagsList() {
      return this.data.tagsList || [];
    },
    hasTagsList() {
      return this.tagsList.length > 0;
    },
    isHomePage() {
      return this.pageKey === 'home';
    },
    withMainTitle() {
      return this.data.withMainTitle !== false;
    },
    hasFirstTitle() {
      return this.withMainTitle && this.firstTitle.length > 0;
    },
  },
  methods: {
    b(element, modifiers) {
      return b(element, modifiers);
    },
    openFirstTitleModal() {
      if (this.isEditMode) {
        this.firstTitleModalVisible = true;
      }
    },
    openSecondTitleModal() {
      if (this.isEditMode) {
        this.secondTitleModalVisible = true;
      }
    },
    openDescriptionModal() {
      if (this.isEditMode) {
        this.descriptionModalVisible = true;
      }
    },
    openButtonModal() {
      if (this.isEditMode) {
        this.buttonModalVisible = true;
      }
    },
    openImageModal() {
      if (this.isEditMode) {
        this.imageModalVisible = true;
      }
    },
    toggleAutograph() {
      if (this.isEditMode) {
        this.$emit('update', { withAutograph: !this.withAutograph });
      }
    },
    toggleAutographColor() {
      if (this.isEditMode) {
        this.$emit('update', { isWhiteSignature: !this.isWhiteSignature });
      }
    },
    handleFirstTitleSave(values) {
      this.$emit('update', { firstTitle: values });
      this.firstTitleModalVisible = false;
    },
    handleFirstTitleDelete() {
      this.$emit('update', { firstTitle: [] });
      this.firstTitleModalVisible = false;
    },
    handleSecondTitleSave(values) {
      this.$emit('update', { secondTitle: values });
      this.secondTitleModalVisible = false;
    },
    handleSecondTitleDelete() {
      this.$emit('update', { secondTitle: [] });
      this.secondTitleModalVisible = false;
    },
    handleDescriptionSave(values) {
      this.$emit('update', {
        description: values.description,
        descriptionMobile: values.descriptionMobile,
      });
      this.descriptionModalVisible = false;
    },
    handleDescriptionDelete() {
      this.$emit('update', { description: '', descriptionMobile: '' });
      this.descriptionModalVisible = false;
    },
    handleButtonSave(values) {
      this.$emit('update', {
        buttonText: values.buttonText,
        buttonTextMobile: values.buttonTextMobile,
        buttonLink: values.buttonLink,
      });
      this.buttonModalVisible = false;
    },
    handleButtonDelete() {
      this.$emit('update', {
        buttonText: '',
        buttonTextMobile: '',
        buttonLink: '',
      });
      this.buttonModalVisible = false;
    },
    handleImageSave(values) {
      this.$emit('update', {
        mediaData: {
          ...this.mediaData,
          src: values.image || this.mediaData.src,
          srcMobile: values.imageMobile || this.mediaData.srcMobile,
        },
      });
      this.imageModalVisible = false;
    },
    handleImageDelete() {
      this.$emit('update', { mediaData: { src: '', srcMobile: '' } });
      this.imageModalVisible = false;
    },
    togglePosition() {
      if (this.isEditMode) {
        this.$emit('update', { isRight: !this.isRight });
      }
    },
    toggleMainTitle() {
      if (this.isEditMode) {
        this.$emit('update', { withMainTitle: !this.withMainTitle });
      }
    },
  },
};
</script>

<template>
  <article :class="b('', { isMobileView, isEditMode, withGrayBackground })">
    <!-- Position toggle (edit mode only) -->
    <div v-if="isEditMode && !isMobileView" :class="b('toggle-controls')">
      <div :class="b('toggle-item')">
        <span :class="b('toggle-label')">Position:</span>
        <a-button
          size="small"
          :type="!isRight ? 'primary' : 'default'"
          @click="togglePosition"
        >
          Info Left
        </a-button>
        <a-button
          size="small"
          :type="isRight ? 'primary' : 'default'"
          @click="togglePosition"
        >
          Info Right
        </a-button>
      </div>
      <div :class="b('toggle-item')">
        <span :class="b('toggle-label')">Main Title:</span>
        <a-switch
          :checked="withMainTitle"
          @change="toggleMainTitle"
        />
      </div>
    </div>

    <!-- First Title (above the main block) -->
    <div
      v-if="hasFirstTitle || (isEditMode && withMainTitle)"
      :class="b('first-title-wrap', { isMobileView, editable: isEditMode })"
      :role="isEditMode ? 'button' : undefined"
      :tabindex="isEditMode ? 0 : undefined"
      @click="openFirstTitleModal"
      @keyup.enter="openFirstTitleModal"
      @keyup.space="openFirstTitleModal"
    >
      <TitleWithLinesBlock
        v-if="firstTitle.length"
        :class="b('first-title', { isMobileView })"
        :is-mobile="isMobile"
        :is-mobile-view="isMobileView"
        :text-list="firstTitle"
      />
      <div v-else-if="isEditMode" :class="b('add-field')">
        + Add first title
      </div>
      <PencilIcon
        v-if="isEditMode && firstTitle.length"
        :class="b('edit-icon')"
        :color="withGrayBackground ? '#FFFFFF' : '#293241'"
        :size="20"
      />
    </div>

    <!-- Main container with background image -->
    <div :class="b('container', { right: isRight, isMobileView })">
      <!-- Background image placeholder -->
      <div
        :class="b('image-placeholder', {
          'icon-left': isRight,
          'icon-right': !isRight,
          editable: isEditMode,
          isMobileView,
        })"
        :style="displayImage ? {
          backgroundImage: `url(${displayImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        } : {}"
        :role="isEditMode ? 'button' : undefined"
        :tabindex="isEditMode ? 0 : undefined"
        @click="openImageModal"
        @keyup.enter="openImageModal"
        @keyup.space="openImageModal"
      >
        <span v-if="!displayImage && isEditMode" :class="b('placeholder-text')">
          <span :class="b('placeholder-plus')">+</span>
          <br>CLICK TO UPLOAD
        </span>
      </div>

      <!-- Info block overlay -->
      <div :class="b('info-block', {
        left: !isRight,
        isMobileView,
        gray: isMobileView && withGrayBackground
      })">
        <div :class="b('info-content')">
          <!-- Second Title (required) -->
          <div
            :class="b('second-title-wrap', { editable: isEditMode })"
            :role="isEditMode ? 'button' : undefined"
            :tabindex="isEditMode ? 0 : undefined"
            @click="openSecondTitleModal"
            @keyup.enter="openSecondTitleModal"
            @keyup.space="openSecondTitleModal"
          >
            <TitleWithLinesBlock
              v-if="secondTitle.length"
              :class="b('second-title', { isMobileView })"
              :is-mobile="isMobile"
              :is-mobile-view="isMobileView"
              :text-list="secondTitle"
            />
            <div v-else :class="b('second-title-placeholder')">
              Second Title
            </div>
            <PencilIcon
              v-if="isEditMode"
              :class="b('edit-icon')"
              color="#293241"
              :size="20"
            />
          </div>

          <!-- Description (optional) -->
          <div
            v-if="hasDescription || isEditMode"
            :class="b('description-wrap', { editable: isEditMode })"
            :role="isEditMode ? 'button' : undefined"
            :tabindex="isEditMode ? 0 : undefined"
            @click="openDescriptionModal"
            @keyup.enter="openDescriptionModal"
            @keyup.space="openDescriptionModal"
          >
            <div v-if="hasDescription" :class="b('description', {
              isMobileView,
              white: withGrayBackground && isMobileView
            })">
              {{ displayDescription }}
            </div>
            <div v-else-if="isEditMode" :class="b('add-field')">
              + Add description
            </div>
            <PencilIcon
              v-if="isEditMode && hasDescription"
              :class="b('edit-icon')"
              color="#293241"
              :size="18"
            />
          </div>

          <!-- Autograph (optional) -->
          <div
            v-if="withAutograph || isEditMode"
            :class="b('autograph-wrap', { editable: isEditMode })"
          >
            <CustomImage
              v-if="withAutograph"
              :class="b('autograph', { white: isWhiteSignature, isMobileView })"
              :image-uri="isWhiteSignature ? autographWhite : autographBlack"
              alt="autograph"
              :without-shadow="true"
            />
            <div v-if="isEditMode" :class="b('autograph-controls')">
              <a-button
                v-if="!withAutograph"
                :class="b('add-field')"
                type="text"
                @click="toggleAutograph"
              >
                + Add autograph
              </a-button>
              <template v-else>
                <a-button
                  size="small"
                  :type="isWhiteSignature ? 'default' : 'primary'"
                  @click="toggleAutographColor"
                >
                  {{ isWhiteSignature ? 'White' : 'Black' }}
                </a-button>
                <a-button
                  size="small"
                  danger
                  @click="toggleAutograph"
                >
                  Remove
                </a-button>
              </template>
            </div>
          </div>
        </div>

        <!-- Button (optional) -->
        <div
          v-if="hasButton || isEditMode"
          :class="b('button-wrap', { editable: isEditMode })"
          :role="isEditMode ? 'button' : undefined"
          :tabindex="isEditMode ? 0 : undefined"
          @click="openButtonModal"
          @keyup.enter="openButtonModal"
          @keyup.space="openButtonModal"
        >
          <div
            v-if="hasButton"
            :class="b('button', { dark: isDarkButton, isMobileView, homePage: isHomePage })"
          >
            {{ displayButtonText }}
            <PencilIcon
              v-if="isEditMode"
              :class="b('edit-icon', { inline: true })"
              :color="isHomePage ? '#ffffff' : '#293241'"
              :size="16"
            />
          </div>
          <div v-else-if="isEditMode" :class="b('add-field')">
            + Add button
          </div>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <EditTitleWithLinesModal
      :visible="firstTitleModalVisible"
      :data="firstTitle"
      :show-delete="firstTitle.length > 0"
      @save="handleFirstTitleSave"
      @cancel="firstTitleModalVisible = false"
      @delete="handleFirstTitleDelete"
    />

    <EditTitleWithLinesModal
      :visible="secondTitleModalVisible"
      :data="secondTitle"
      :show-delete="secondTitle.length > 0"
      @save="handleSecondTitleSave"
      @cancel="secondTitleModalVisible = false"
      @delete="handleSecondTitleDelete"
    />

    <EditDescriptionModal
      :visible="descriptionModalVisible"
      :description="description"
      :description-mobile="descriptionMobile"
      @close="descriptionModalVisible = false"
      @save="handleDescriptionSave"
      @delete="handleDescriptionDelete"
    />

    <EditButtonModal
      :visible="buttonModalVisible"
      :button-text="buttonText"
      :button-text-mobile="buttonTextMobile"
      :button-link="buttonLink"
      @close="buttonModalVisible = false"
      @save="handleButtonSave"
      @delete="handleButtonDelete"
    />

    <ImageUploadModal
      :visible="imageModalVisible"
      :image="mediaData.src"
      :image-mobile="mediaData.srcMobile"
      :show-delete="!!mediaData.src"
      @close="imageModalVisible = false"
      @save="handleImageSave"
      @delete="handleImageDelete"
    />
  </article>
</template>
